import type * as CF from '@sa/services/interfaces/customFunction';
import { enqueueCall } from './batch.js';
import { excelToJSDate, flattenExcelRange, validatePositiveNumber } from '$lib/office/excelHelpers.js';

/**
 * Connects to QuickBooks and sums the GL account balance for an account filtered between two dates and by class.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account QuickBooks GL account name or code to process.
 * @param Class The class to filter.  Leaving this blank will use all transactions without a specified class.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeSubClasses] Optional. Include sub-classes in the sum.
 * @returns Sum of accounts
 */
export function QCLASS(
    ScottsOrgId: string,
    Account: string,
    Class: string,
    StartDate: number,
    EndDate: number,
    IncludeSubClasses?: boolean,
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QCLASS = {
        funcName: 'QCLASS',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        class: Class,
        includeSub: IncludeSubClasses,
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balances for a range of account codes filtered between two dates and by class.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartAccountCode First Account code to process.
 * @param EndAccountCode Last Account code to process.
 * @param Class The class to filter.  Leaving this blank will use all transactions without a specified class.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeArchived] Optional. Include all accounts in the Range, not just the active ones.
 * @param [IncludeSubClasses] Optional. Include sub-classes in the sum.
 * @returns Sum of accounts
 */
export function QCLASSR(
    ScottsOrgId: string,
    StartAccountCode: number,
    EndAccountCode: number,
    Class: string,
    StartDate: number,
    EndDate: number,
    IncludeArchived?: boolean,
    IncludeSubClasses?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (!validatePositiveNumber(StartAccountCode)) {
        return OfficeExtension.Promise.reject('Start code is not valid.');
    }
    if (!validatePositiveNumber(EndAccountCode)) {
        return OfficeExtension.Promise.reject('End code is not valid.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QCLASSR = {
        funcName: 'QCLASSR',
        scottsOrgId: ScottsOrgId,
        startAcct: StartAccountCode,
        endCode: EndAccountCode,
        startDate: startDateString,
        endDate: endDateString,
        class: Class,
        includeAll: IncludeArchived,
        includeSub: IncludeSubClasses,
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balance for an account filtered between two dates and by department.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account QuickBooks GL account name or code to process.
 * @param Department The department to filter.  Leaving this blank will use all transactions without a specified department.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeSubDepts] Optional. Include sub-departments in the sum.
 * @returns Sum of accounts
 */
export function QDEPT(
    ScottsOrgId: string,
    Account: string,
    Department: string,
    StartDate: number,
    EndDate: number,
    IncludeSubDepts?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QDEPT = {
        funcName: 'QDEPT',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        department: Department,
        includeSub: IncludeSubDepts
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balances for a range of account codes filtered between two dates and by department.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartAccountCode First Account code to process.
 * @param EndAccountCode Last Account code to process.
 * @param Department The department to filter.  Leaving this blank will use all transactions without a specified department.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeArchived] Optional. Include all accounts in the Range, not just the active ones.
 * @param [IncludeSubDepts] Optional. Include sub-departments in the sum.
 * @returns Sum of accounts
 */
export function QDEPTR(
    ScottsOrgId: string,
    StartAccountCode: number,
    EndAccountCode: number,
    Department: string,
    StartDate: number,
    EndDate: number,
    IncludeArchived?: boolean,
    IncludeSubDepts?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (!validatePositiveNumber(StartAccountCode)) {
        return OfficeExtension.Promise.reject('Start code is not valid.');
    }
    if (!validatePositiveNumber(EndAccountCode)) {
        return OfficeExtension.Promise.reject('End code is not valid.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QDEPTR = {
        funcName: 'QDEPTR',
        scottsOrgId: ScottsOrgId,
        startAcct: StartAccountCode,
        endCode: EndAccountCode,
        startDate: startDateString,
        endDate: endDateString,
        department: Department,
        includeAll: IncludeArchived,
        includeSub: IncludeSubDepts
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balance for an account filtered between two dates, by class, and by department.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account QuickBooks GL account name or code to process.
 * @param Class The class to filter.
 * @param Department The department to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeSubClassAndDepts] Optional. Include sub-classes and sub-departments in the sum.
 * @returns Sum of accounts
 */
export function QCLASS_DEPT(
    ScottsOrgId: string,
    Account: string,
    Class: string,
    Department: string,
    StartDate: number,
    EndDate: number,
    IncludeSubClassAndDepts?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QCLASS_DEPT = {
        funcName: 'QCLASS_DEPT',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        class: Class,
        department: Department,
        includeSub: IncludeSubClassAndDepts
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balances for an account filtered between two dates and by customer.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account QuickBooks GL account name or code to process.
 * @param Customer The customer to filter.  Leaving this blank will use all transactions without a specified customer.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeSubCustomers] Optional. Include sub-customers in the sum.
 * @returns Sum of accounts
 */
export function QCUSTOMER(
    ScottsOrgId: string,
    Account: string,
    Customer: string,
    StartDate: number,
    EndDate: number,
    IncludeSubCustomers?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QCUSTOMER = {
        funcName: 'QCUSTOMER',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        customer: Customer,
        includeSub: IncludeSubCustomers
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balances for an account filtered between two dates and by vendor.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account QuickBooks GL account name or code to process.
 * @param Vendor The vendor to filter.  Leaving this blank will use all transactions without a specified vendor.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @returns Sum of accounts
 */
export function QVENDOR(
    ScottsOrgId: string,
    Account: string,
    Vendor: string,
    StartDate: number,
    EndDate: number
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QVENDOR = {
        funcName: 'QVENDOR',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        vendor: Vendor
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the budgets for an account filtered between two dates, by class, by department, and by customer.  Wildcard "*" supported for this function.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param BudgetName Name of the budget to pull.
 * @param Account QuickBooks GL account name or code to process.
 * @param Class The class to filter.
 * @param Department The department to filter.
 * @param Customer The customer to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeSubThings] Optional. Include sub-classes, sub-departments, and sub-customers in the sum.
 * @returns Sum of accounts
 */
export function QBUDGETALL(
    ScottsOrgId: string,
    BudgetName: string,
    Account: string,
    Class: string,
    Department: string,
    Customer: string,
    StartDate: number,
    EndDate: number,
    IncludeSubThings?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (BudgetName == null || BudgetName == '') {
        return OfficeExtension.Promise.reject('Budget is missing.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QBUDGETALL = {
        funcName: 'QBUDGETALL',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        budgetName: BudgetName,
        class: Class,
        department: Department,
        customer: Customer,
        includeSub: IncludeSubThings
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the budgets for many accounts filtered between two dates, by class, by department, and by customer.  Wildcard "*" supported for this function.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param BudgetName Name of the budget to pull.
 * @param StartAccountCode First Account Code to process.  This code must be numeric.
 * @param EndAccountCode Last Account Code to process.  This code must be numeric.
 * @param Class The class to filter.
 * @param Department The department to filter.
 * @param Customer The customer to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeSubThings] Optional. Include sub-classes, sub-departments, and sub-customers in the sum.
 * @returns Sum of accounts
 */
export function QBUDGETALLR(
    ScottsOrgId: string,
    BudgetName: string,
    StartAccountCode: number,
    EndAccountCode: number,
    Class: string,
    Department: string,
    Customer: string,
    StartDate: number,
    EndDate: number,
    IncludeSubThings?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (BudgetName == null || BudgetName == '') {
        return OfficeExtension.Promise.reject('Budget is missing.');
    }
    if (!validatePositiveNumber(StartAccountCode)) {
        return OfficeExtension.Promise.reject('Start code is not valid.');
    }
    if (!validatePositiveNumber(EndAccountCode)) {
        return OfficeExtension.Promise.reject('End code is not valid.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QBUDGETALLR = {
        funcName: 'QBUDGETALLR',
        scottsOrgId: ScottsOrgId,
        startAcct: StartAccountCode,
        endCode: EndAccountCode,
        startDate: startDateString,
        endDate: endDateString,
        budgetName: BudgetName,
        class: Class,
        department: Department,
        customer: Customer,
        includeSub: IncludeSubThings
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the budgets for many accounts filtered between two dates, by class, by department, and by customer.  Wildcard "*" supported for this function.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param BudgetName Name of the budget to pull.
 * @param Class The class to filter.
 * @param Department The department to filter.
 * @param Customer The customer to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param IncludeSubThings Include sub-classes, sub-departments, and sub-customers in the sum.
 * @param Accounts GL account Names or Codes to process.
 * @returns Sum of accounts
 */
export function QBUDGETALLMULTI(
    ScottsOrgId: string,
    BudgetName: string,
    Class: string,
    Department: string,
    Customer: string,
    StartDate: number,
    EndDate: number,
    IncludeSubThings: boolean,
    Accounts: string[][][]
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (BudgetName == null || BudgetName == '') {
        return OfficeExtension.Promise.reject('Budget is missing.');
    }
    const accounts = flattenExcelRange(Accounts);
    if (accounts.length === 0) {
        return OfficeExtension.Promise.reject('Accounts are missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QBUDGETALLMULTI = {
        funcName: 'QBUDGETALLMULTI',
        scottsOrgId: ScottsOrgId,
        budgetName: BudgetName,
        class: Class,
        department: Department,
        customer: Customer,
        startDate: startDateString,
        endDate: endDateString,
        includeSub: IncludeSubThings,
        accounts: accounts,
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balance for an account filtered between two dates, by class, by customer, by department, or by vendor.  Wildcard "*" supported for this function.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account QuickBooks GL account name or code to process.
 * @param Class The class to filter.
 * @param Customer The customer to filter.
 * @param Department The department to filter.
 * @param Vendor The vendor to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeSubThings] Optional. Include sub-classes, sub-departments, and sub-customers in the sum.
 * @returns Sum of accounts
 */
export function QALL(
    ScottsOrgId: string,
    Account: string,
    Class: string,
    Customer: string,
    Department: string,
    Vendor: string,
    StartDate: number,
    EndDate: number,
    IncludeSubThings?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QALL = {
        funcName: 'QALL',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        class: Class,
        customer: Customer,
        department: Department,
        vendor: Vendor,
        includeSub: IncludeSubThings
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balance for many accounts filtered between two dates, by class, by customer, by department, or by vendor.  Wildcard "*" supported for this function.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartAccountCode First Account Code to process.  This code must be numeric.
 * @param EndAccountCode Last Account Code to process.  This code must be numeric.
 * @param Class The class to filter.
 * @param Customer The customer to filter.
 * @param Department The department to filter.
 * @param Vendor The vendor to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeSubThings] Optional. Include sub-classes, sub-departments, and sub-customers in the sum.
 * @returns Sum of accounts
 */
export function QALLR(
    ScottsOrgId: string,
    StartAccountCode: number,
    EndAccountCode: number,
    Class: string,
    Customer: string,
    Department: string,
    Vendor: string,
    StartDate: number,
    EndDate: number,
    IncludeSubThings?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (!validatePositiveNumber(StartAccountCode)) {
        return OfficeExtension.Promise.reject('Start code is not valid.');
    }
    if (!validatePositiveNumber(EndAccountCode)) {
        return OfficeExtension.Promise.reject('End code is not valid.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QALLR = {
        funcName: 'QALLR',
        scottsOrgId: ScottsOrgId,
        startAcct: StartAccountCode,
        endCode: EndAccountCode,
        startDate: startDateString,
        endDate: endDateString,
        class: Class,
        customer: Customer,
        department: Department,
        vendor: Vendor,
        includeSub: IncludeSubThings
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and sums the GL account balance for many accounts filtered between two dates, by class, by customer, by department, or by vendor.  Wildcard "*" supported for this function.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Class The class to filter.
 * @param Customer The customer to filter.
 * @param Department The department to filter.
 * @param Vendor The vendor to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param IncludeSubThings Optional. Include sub-classes, sub-departments, and sub-customers in the sum.
 * @param Accounts GL account Names or Codes to process.
 * @returns Sum of accounts
 */
export function QALLMULTI(
    ScottsOrgId: string,
    Class: string,
    Customer: string,
    Department: string,
    Vendor: string,
    StartDate: number,
    EndDate: number,
    IncludeSubThings: boolean,
    Accounts: string[][][]
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const accounts = flattenExcelRange(Accounts);
    if (accounts.length === 0) {
        return OfficeExtension.Promise.reject('Accounts are missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QALLMULTI = {
        funcName: 'QALLMULTI',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        class: Class,
        customer: Customer,
        department: Department,
        vendor: Vendor,
        includeSub: IncludeSubThings,
        accounts: accounts,
    };
    return enqueueCall(body);
}

/**
 * Connects to QuickBooks and returns the Net Income as reported by the P&L Report between two dates, by class, by customer, by department, or by vendor.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Class The class to filter.  Leave blank for all.
 * @param Customer The customer to filter.  Leave blank for all.
 * @param Department The department to filter.  Leave blank for all.
 * @param Vendor The vendor to filter.  Leave blank for all.
 * @param StartDate Starting date of the range.
 * @param EndDate Final date of the range.
 * @param [IncludeSubThings] Optional. Include sub-classes, sub-departments, and sub-customers in the Net Income.
 * @returns QuickBook's Net Income
 */
export function QNI(
    ScottsOrgId: string,
    Class: string,
    Customer: string,
    Department: string,
    Vendor: string,
    StartDate: number,
    EndDate: number,
    IncludeSubThings?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.QNI = {
        funcName: 'QNI',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        class: Class,
        customer: Customer,
        department: Department,
        vendor: Vendor,
        includeSub: IncludeSubThings
    };
    return enqueueCall(body);
}